import {Inject, Injectable} from '@angular/core';
import {of} from 'rxjs/observable/of';
import {deepExtend} from '../../../../utils/object/extend.utils';
import {EnvVariables} from '../../../environment-variables/environment-variables.token';
import {Observable} from 'rxjs/Observable';
import 'rxjs/add/operator/map';
import 'rxjs/add/operator/share';
import 'rxjs/add/operator/catch';
import 'rxjs/add/observable/throw';
import {App, Loading, LoadingController, ToastController} from 'ionic-angular';
import {WindowService} from '../window/window.service';
import {PrismConfigOptions} from '../../../environment-variables/environment-variables.interface';
import {HttpClient, HttpErrorResponse, HttpHeaders} from '@angular/common/http';
import {catchError, map, share} from 'rxjs/operators';

/**
 * Abstract class that provides wrappers for the http get and post call
 * @class
 * @abstract
 */
@Injectable()
export abstract class AbstractAdvTechService {

  /**
   * Number of requests being made to the server currently in progress
   * @protected
   * @type {Array}
   */
  protected inFlight: number[] = [];

  /**
   * The base prism url string
   * @type string
   * @protected
   */
  protected hubUrl: string;

  /**
   * The prefix for the url
   * @type {string}
   * @protected
   */
  protected prefix: string;

  /**
   * Parameters to be passed with the XHR call
   * @type {object}
   * @protected
   */
  protected params: {} = {};

  /**
   * The message for the loading control
   * @type {string}
   * @protected
   */
  protected loadingMessage: string = '';

  /**
   * The created loading control
   * @type {Loading}
   * @protected
   */
  protected loading: Loading;

  /**
   * Should skip the access denied redirect
   * @type {boolean}
   */
  protected skipAccessDeniedRedirect: boolean = false;

  /**
   * @constructor
   * @param {EnvVariables} envVariables EnvVariables instance
   * @param {App} app App instance
   * @param {Http} http Http Instance
   * @param {LoadingController} loadingCtrl LoadingController Instance
   * @param {ToastController} toastCtrl ToastController instance
   * @param {WindowService} windowService Window instance
   */
  constructor(@Inject(EnvVariables) public envVariables: PrismConfigOptions,
              protected app: App,
              protected http: HttpClient,
              protected loadingCtrl: LoadingController,
              protected toastCtrl: ToastController,
              public windowService: WindowService) {
    this.hubUrl = envVariables.hubApi;
  }

  /**
   * Sets the request params
   * @param {object} value request properties
   * @returns {this}
   * @protected
   */
  protected setParams(value): this {
    const body = new URLSearchParams(),
      keys = Object.keys(value);

    for (const k of keys) {
      if (Array.isArray(value[k])) {
        for (const item of value[k]) {
          body.append(k, item);
        }
      }
      else {
        body.set(k, value[k]);
      }
    }

    this.params = body.toString();

    return this;
  }

  /**
   * Sets the loading dialog string
   * @protected
   * @param {string} message message for the loading controller
   * @returns {this}
   */
  protected setLoadingMessage(message): this {
    this.loadingMessage = message;

    return this;
  }

  /**
   * Presents the loading control if it isn't already presented
   * @protected
   * @returns {Promise<any>}
   */
  protected presentLoading(): Promise<any> {
    if (!this.loading) {
      this.loading = this.loadingCtrl.create({
        content: this.loadingMessage,
      });
      this.inFlight.push(1);
    }

    return this.loading.present();
  }

  /**
   * Dismisses the loading control
   * @protected
   * @returns {void}
   */
  protected dismissLoading(): void {
    this.inFlight.pop();
    if (this.inFlight.length === 0) {
      this.loading && this.loading.dismiss();
      this.loading = null;
      this.setLoadingMessage('');
    }
  }

  /**
   * Abstract method to handle call success
   * @param {object} data value from the api response
   * @protected
   * @returns {void}
   */
  protected hCallSuccess(data): void {
    this.windowService.window.activeXHRRequests--;
    console.log('Success:');
    console.log(data);
  }

  /**
   * Abstract method to handle call errors
   * @param {object} data value from the api response
   * @protected
   * @returns {void}
   */
  protected hCallError(data): void {
    this.windowService.window.activeXHRRequests--;
    console.log('Error:');
    console.log(data);
  }

  /**
   * Provides a basic subscription to the calls
   * @param {Observable<any>} observable passed in from the get and post endpoint functions
   * @protected
   * @returns {Observable<any>}
   */
  protected subscribeToCall(observable: Observable<any>) {
    this.windowService.window.activeXHRRequests++;
    observable.subscribe(
      (data) => this.hCallSuccess(data),
      (err) => this.hCallError(err),
      () => this.dismissLoading(),
    );
  }

  /**
   * Handles the map operation on the observables
   * @param {Response} res response from the api call
   * @returns {any}
   * @protected
   */

  protected hMap(res: Response): any {
    try {
      return res.json();
    }
    catch (e) {
      return res;
    }
  }

  /**
   * Sets the skipAccessDeniedRedirect property on the class
   * @param {boolean} skip should skip the redirect
   * @returns {void}
   */
  protected setSkipAccessDeniedRedirect(skip: boolean = false): void {
    this.skipAccessDeniedRedirect = skip;
  }

  /**
   * Handles the catch operation on the observables
   * @param {Response} err response form the api call
   * @param {Observable<any>} source
   * @returns {ErrorObservable}
   * @protected
   */
  protected hCatch(err: HttpErrorResponse, source: Observable<any>): Observable<any> {
    this.dismissLoading();
    if (err.status === 200) {
      return of(err.error);
    }

    if (typeof err.error === 'object') {
      return Observable.throw(err.error);
    }

    try {
      return Observable.throw(JSON.parse(err.error));
    }
    catch (e) {
      this.toastCtrl.create({
        duration: 2000,
        message: 'Call failed',
        position: 'bottom',
      }).present();
      return Observable.throw({error: 'Call failed'});
    }
  }

  /**
   * builds the hub url
   * @param {string} url part of the url after the prefix
   * @param {boolean} skipPrefix should skip the prefix appending
   * @returns {string}
   * @protected
   */
  protected getUrl(url, skipPrefix) {
    if (this.prefix && !skipPrefix) {
      return `${this.hubUrl}/${this.prefix}/${url}`;
    }
    return `${this.hubUrl}/${url}`;
  }

  /**
   * Performs a post request
   * @param {string} url endpoint for the call
   * @param {boolean} skipLoading should skip the loading controller
   * @param {boolean} skipPrefix should skip adding the prefix to the url
   * @param {{}} body of post
   * @returns {Observable<any>}
   * @protected
   */
  protected callPostEndpoint(url: string, skipLoading: boolean = false, skipPrefix: boolean = false, body: {} = null): Observable<any> {
    const headers = this.getPostHeaders();
    const options = {headers, withCredentials: true};
    const observable = this.doPostCall(url, skipPrefix, options, body);

    if (!skipLoading) {
      this.presentLoading();
    }
    this.subscribeToCall(observable);

    return observable;
  }

  /**
   * Get the POST headers
   * @returns {Headers}
   * @protected
   */
  protected getPostHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
      'X-Requested-With': 'XMLHttpRequest',
    });
  }

  /**
   * Post to the url
   * @param {string} url The url to hit
   * @param {boolean} skipPrefix Skip the url prefix
   * @param {{}} options The request options
   * @param {{}} body Request body
   * @returns {Observable<any>}
   */
  protected doPostCall(url: string, skipPrefix: boolean, options: {}, body: {} = null): Observable<any> {
    return this.http.post<Response>(this.getUrl(url, skipPrefix), body || this.params, options).pipe(
      map((res) => this.hMap(res)),
      catchError((error, source) => this.hCatch(error, source)),
      share(),
    );
  }

  /**
   * Performs a get request
   * @param {string} url endpoint for the call
   * @param {boolean} skipLoading should skip the loading controller
   * @param {boolean} skipPrefix should skip adding the prefix to the url
   * @returns {Observable<any>}
   * @protected
   */
  protected callGetEndpoint(url: string, skipLoading: boolean = false, skipPrefix: boolean = false): Observable<any> {
    const headers = this.getGetHeaders();
    const options = {
      headers,
      withCredentials: true,
      search: this.params,
    };
    const observable = this.doGetCall(url, skipPrefix, options);

    if (!skipLoading) {
      this.presentLoading();
    }
    this.subscribeToCall(observable);

    return observable;
  }

  /**
   * get the GET request headers
   * @returns {Headers}
   * @protected
   */
  protected getGetHeaders(): HttpHeaders {
    return new HttpHeaders({
      'X-Requested-With': 'XMLHttpRequest',
    });
  }

  /**
   * Perform a GET call on the specified url
   * @param {string} url The url to hit
   * @param {boolean} skipPrefix To skip the url prefix or not
   * @param {{}} options The Request Options
   * @returns {Observable<any>}
   * @protected
   */
  protected doGetCall(url: string, skipPrefix: boolean, options: {}): Observable<any> {
    options = deepExtend({params: {siteId: this.envVariables.siteId}}, [options]);
    return this.http.get<Response>(this.getUrl(url, skipPrefix), options).pipe(
      map((res) => this.hMap(res)),
      catchError((err, source) => this.hCatch(err, source)),
      share());
  }
}



// WEBPACK FOOTER //
// ./src/app/shared/services/advtech/abstract-advtech.service.ts